import { useEffect, useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** A line under the title — what the dialog is about (a paper's name). */
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Dismiss by clicking the backdrop, or by swiping the sheet down on mobile.
   *
   * OFF by default. Most dialogs here hold something half-entered — a date, a
   * confidence rating, a roster half-picked — and a stray click on the backdrop
   * threw it away with no warning and no undo. Opt in for a dialog that holds
   * nothing: a preview, a replay, an image.
   */
  closeOnOverlayClick?: boolean;
  /** Dismiss on Escape. Off by default, for the same reason. */
  closeOnEscape?: boolean;
  /** Extra class on the modal box, for callers that need to size it themselves. */
  className?: string;
}

// Distance the user must drag down before the modal dismisses
const DRAG_DISMISS_THRESHOLD = 80;

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  children,
  footer,
  closeOnOverlayClick = false,
  closeOnEscape = false,
  className,
}: ModalProps) {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    },
    [onClose, closeOnEscape]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  // ── Swipe-down-to-dismiss (mobile bottom sheet only) ──────────────
  const dragStartY = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  const isMobileBottomSheet = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

  const handleTouchStart = (e: React.TouchEvent) => {
    // Gated with the backdrop: swiping a sheet away is the same accident as
    // clicking behind it, and a dialog that refuses one should refuse the other.
    if (!closeOnOverlayClick) return;
    if (!isMobileBottomSheet()) return;
    dragStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    // Only allow downward drag
    setDragOffset(Math.max(0, delta));
  };

  const handleTouchEnd = () => {
    if (dragStartY.current === null) return;
    if (dragOffset > DRAG_DISMISS_THRESHOLD) {
      onClose();
    }
    dragStartY.current = null;
    setDragOffset(0);
  };

  // Reset drag state when modal closes
  useEffect(() => {
    if (!isOpen) {
      dragStartY.current = null;
      setDragOffset(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  const modalStyle = dragOffset > 0
    ? { transform: `translateY(${dragOffset}px)`, transition: 'none' }
    : undefined;

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className={`modal modal-${size}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        style={modalStyle}
      >
        <div
          className="modal-drag-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          aria-hidden="true"
        />
        {title && (
          <div
            className="modal-header"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="modal-heading">
              <h2 id="modal-title" className="modal-title">
                {title}
              </h2>
              {subtitle && <p className="modal-subtitle">{subtitle}</p>}
            </div>
            <button className="modal-close" onClick={onClose} aria-label="Close modal">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
